import type { IRecord, IUserRef } from '@platform/core/types';
import { valueOf } from './valueOf';

/** Поле виконавця: сервер веде, хто призначив чинного виконавця (`assigned_by`, privacy.md). */
const ASSIGNEE = 'assignee';

/** Значення для порівняння й API: користувач → ідентифікатор, порожній рядок і undefined → null. Чиста функція. */
export function normalizeValue(value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value) && 'id' in (value as IUserRef)) return (value as IUserRef).id;
  return value === '' || value === undefined ? null : value;
}

/** Чи однакові два значення поля після нормалізації (числа й числові рядки, масиви — поелементно). Чиста функція. */
export function sameValue(a: unknown, b: unknown): boolean {
  const x = normalizeValue(a);
  const y = normalizeValue(b);
  if (Array.isArray(x) || Array.isArray(y)) {
    return Array.isArray(x) && Array.isArray(y) && x.length === y.length && x.every((v, i) => sameValue(v, y[i]));
  }
  if (typeof x === 'number' || typeof y === 'number') return x !== null && y !== null && Number(x) === Number(y);
  return x === y;
}

/**
 * Чи є значення чернетки повторним призначенням (рішення 1 етапу 10): Owner/Admin простору (`reassigner` — його ідентифікатор, reassignerOf)
 * явно вибрав у полі «Виконавець» збереженого запису того, хто вже виконавець, і це не він сам. Такий вибір — зміна: «Зберегти · 1»
 * надсилає {values: {assignee}}, сервер записує Owner/Admin як того, хто призначив, і учасник, що сам себе призначив, бачить персональні
 * дані після одного збереження (privacy.md, Issue 042 #42). Без ролі Owner/Admin, вибір себе, порожній виконавець, інше поле чи новий
 * запис — не повторне призначення. Чиста функція.
 */
export function isReassignment(record: IRecord | undefined, key: string, value: unknown, reassigner?: string): boolean {
  if (key !== ASSIGNEE || !reassigner || !record?.id) return false;
  const picked = normalizeValue(value);
  return typeof picked === 'string' && picked !== reassigner && sameValue(picked, valueOf(record, {}, key));
}

/**
 * Чи значення поля — зміна, яку зберігають (одне правило для всіх поверхонь правки: лічильник «Зберегти · N» повної й контекстної форми,
 * негайні зміни міні-форми, «Призначити мені»; прототип v4 setVal: значення, що дорівнює збереженому, не зберігається): значення
 * відрізняється від збереженого запису або це повторне призначення чинного виконавця Owner/Admin (`reassigner`, isReassignment). Чиста функція.
 */
export function isFieldChange(record: IRecord | undefined, key: string, value: unknown, reassigner?: string): boolean {
  return !sameValue(value, valueOf(record, {}, key)) || isReassignment(record, key, value, reassigner);
}

/**
 * Змінені поля чернетки повної форми (лічильник «Зберегти · N»): ключі, значення яких — зміна (isFieldChange); теги — одним полем
 * `tags`, якщо набір змінено. Повернення значення до початкового знімає поле з переліку — крім явного повторного призначення чинного
 * виконавця Owner/Admin (`reassigner`, isReassignment): воно лишається зміною. Чиста функція.
 */
export function changedKeys(record: IRecord | undefined, draft: Record<string, unknown>, tags: string[] | null, reassigner?: string): string[] {
  const keys = Object.keys(draft).filter((k) => isFieldChange(record, k, draft[k], reassigner));
  if (tags !== null && !sameValue(tags, record?.tags ?? [])) keys.push('tags');
  return keys;
}

/** Значення PATCH чернетки: змінені поля без `tags` (теги йдуть окремо) у вигляді для API (normalizeValue). Чиста функція. */
export function draftValues(keys: string[], draft: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(keys.filter((k) => k !== 'tags').map((k) => [k, normalizeValue(draft[k])]));
}
